import React, { useState, useEffect } from "react";
import classNames from "classnames";
import ViewSiteForm from "forms/ViewSiteForm/ViewSiteForm";
import useBlocks from "hooks/useBlocks";
import BlocksList from "components/BlocksList/BlocksList";
import Chevron from "assets/Chevron";
import { IUserSite, ISite, IBlock, db } from "database";
import styles from "./ViewSite.module.scss";

type ViewSiteProps = {
	close: () => void;
	editSite: () => void;
	selectedSite: IUserSite;
	viewBlock: (block: IBlock) => void;
};

const operationalStatusColor = [
	{ name: "combined", color: "#8dc055" },
	{ name: "demolished", color: "#bb0034" },
	{ name: "operational", color: "#8dc055" },
	{ name: "out of use", color: "#bb0034" },
	{ name: "removed", color: "#bb0034" },
];

const ViewSite: React.FC<ViewSiteProps> = ({
	close,
	selectedSite,
	editSite,
	viewBlock,
}) => {
	const [site, setSite] = useState(selectedSite);
	const [confirmMessage, setConfirmMessage] = useState(false);

	const checkToEditSite = () => {
		if (selectedSite.status === "verified") {
			setConfirmMessage(true);
		} else {
			editSite();
		}
	};

	useEffect(() => {
		const loadSavedFormDetails = async () => {
			if (selectedSite) {
				const savedSite = await db.savedUserSites.get(selectedSite.userSiteId);
				if (savedSite) {
					setSite({
						...selectedSite,
						route: savedSite.route,
						name: savedSite.name,
						altName: savedSite.altName,
						operationalStatus: savedSite.operationalStatus,
						siteType: savedSite.siteType,
						address: savedSite.address,
						postcode: savedSite.postcode,
						elr: savedSite.elr,
						mileage: savedSite.mileage,
						latitude: savedSite.latitude,
						longitude: savedSite.longitude,
						photos: savedSite.photos,
						comments: savedSite.comments,
					});
				} else {
					setSite(selectedSite);
				}
			}
		};

		loadSavedFormDetails();
	}, [selectedSite]);

	const [selectedTab, setSelectedTab] = useState("sites");
	const blocks = useBlocks(selectedSite.siteId);
	const getOperationalColor = (operationalStatus: string): string => {
		const findColor = operationalStatusColor.find(
			(op: any) => op.name === operationalStatus
		);
		return findColor ? findColor.name : "#bb0034";
	};
	return (
		<>
			{confirmMessage && (
				<div
					className={classNames(styles.confirmMessage, {
						[styles.confirmMessageEnter]: confirmMessage,
					})}
				>
					<p>
						This Site has already been verified. Are you sure you want to edit
						it?
					</p>
					<button
						onClick={() => {
							setConfirmMessage(false);
							editSite();
						}}
					>
						Okay
					</button>
					<button onClick={() => setConfirmMessage(false)}>Cancel</button>
				</div>
			)}
			<div className={styles.ViewSite}>
				<div className={styles.MainView}>
					<div className={styles.Header}>
						<div></div>
						{site.operationalStatus ? (
							<div
								className={styles.OperationalStatusContainer}
								style={{
									backgroundColor: getOperationalColor(
										site.operationalStatus.toLowerCase()
									),
								}}
							>
								<p>{site.operationalStatus.toUpperCase()}</p>
							</div>
						) : (
							<div></div>
						)}
						<div className={styles.closeChevron}>
							<Chevron color="grey" onClick={() => close()} />
						</div>
					</div>
					<div className={styles.TabsContainer}>
						<div
							className={styles.SitesTab}
							onClick={() => setSelectedTab("sites")}
						>
							<div
								className={
									selectedTab === "sites"
										? styles.SelectedTab
										: styles.UnselectedTab
								}
							>
								<p>Site</p>
							</div>
						</div>
						<div
							className={styles.BlocksTab}
							onClick={() => setSelectedTab("blocks")}
						>
							<div
								className={
									selectedTab === "blocks"
										? styles.SelectedTab
										: styles.UnselectedTab
								}
							>
								<p>Blocks</p>
								<span
									className={
										selectedTab === "sites"
											? styles.BlockCountContainer
											: styles.SelectedBlockCountContainer
									}
								>
									<p>{blocks ? blocks.length : 0}</p>
								</span>
							</div>
						</div>
					</div>
					{selectedTab === "sites" && (
						<div>
							<div>
								<div>
									<p className={styles.SiteIdHeader}>SITE ID</p>
									<p className={styles.SiteId}>
										{selectedSite.siteId
											? selectedSite.siteId
											: "Not available"}
									</p>
									<p className={styles.SiteIdHeader}>RECORD ID</p>
									<p className={styles.RecordId}>{selectedSite.userSiteId}</p>
								</div>
								<ViewSiteForm closeForm={() => close()} defaultDetails={site} />
							</div>
							<div className={styles.buttonHolder}>
								<button
									type="submit"
									className={
										selectedSite.status === "verified"
											? styles.verified
											: styles.awaitingVerification
									}
									onClick={() => checkToEditSite()}
									disabled={selectedSite.status === "exported" ? true : false}
								>
									{selectedSite.status !== "exported"
										? "Edit and verify site"
										: "Site exported"}
								</button>
							</div>
						</div>
					)}
					{selectedTab === "blocks" && blocks?.length && (
						<div>
							<div>
								<p className={styles.SiteIdHeader}>BLOCKS WITHIN SITE</p>
							</div>
							<BlocksList
								viewBlock={(block) => {
									viewBlock(block);
								}}
								blocks={blocks}
							/>
						</div>
					)}
				</div>
			</div>
		</>
	);
};

export default ViewSite;
